import { useMutation } from "@tanstack/react-query";

import { rooms, type Room } from "./api";

/**
 * Who may put things in the queue, in the console's header.
 *
 * ## Not in the settings dialog, and it has always said so
 *
 * This briefly moved into Room settings along with everything else that was
 * cluttering the deck column, and that was a mistake its own note had already
 * warned against: it is the control a host reaches for *during* a broadcast -
 * the answer to being brigaded - and an answer that takes three clicks to find
 * is not an answer.
 *
 * Everything else in that dialog is decided once a set. This is decided in the
 * middle of one, which is why it sits beside the room's name where the host is
 * already looking.
 *
 * ## Small, because it is not the headline
 *
 * The state matters more than the control: a host glancing up needs to know
 * submissions are open without reading three buttons. So the current one is
 * filled and the others recede, which reads as a status first and a switch
 * second.
 */
export function SubmissionsControl({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (submissions: Room["submissions"]) =>
      rooms.settings({ submissions }),
    onSuccess: onChange,
  });

  const options: { id: Room["submissions"]; label: string }[] = [
    { id: "open", label: "Everyone" },
    { id: "followers", label: "Followers" },
    { id: "closed", label: "Closed" },
  ];

  return (
    <div
      className="flex shrink-0 items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
      role="group"
      aria-label="Who may submit"
      data-submissions-control
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={room.submissions === option.id}
          data-submissions={option.id}
          disabled={update.isPending}
          onClick={() => update.mutate(option.id)}
          className={`rounded-[var(--radius-pill)] px-3 py-1 font-mono text-[11px] transition-colors disabled:opacity-60 ${
            room.submissions === option.id
              ? "bg-accent text-accent-ink"
              : "text-soft hover:bg-raised hover:text-ink"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
